import {
  OffsetPaginationControls,
  SortDirection,
} from '@droidmap/shared-service-contract'

/* ---------------------- Cross-org organisation list ----------------------- */

/**
 * The cross-organisation list is an internal read: it answers for every org
 * on the platform, so only a service caller (an internal-pool token) may ask.
 * The admin service is its consumer; the fields, operators and sort keys are
 * the vocabulary the staff console's table sends, so that a filter chip maps
 * onto a query with no translation layer between them.
 */

/** Fields the list can be filtered on, in wire form (snake_case). */
export const ORGANISATION_LIST_FILTER_FIELDS = [
  'name',
  'credit_plan_tier',
  'lifecycle_state',
  'payment_state',
  'is_test',
  'monthly_spend_cents',
  'asset_count',
  'storage_bytes',
  'created_at',
] as const

export type OrganisationListFilterField =
  (typeof ORGANISATION_LIST_FILTER_FIELDS)[number]

/**
 * Fields the list can be sorted on. The two nullable trial timestamps sort
 * (nulls last) but do not filter: the filter grammar has no null test.
 */
export const ORGANISATION_LIST_SORT_KEYS = [
  ...ORGANISATION_LIST_FILTER_FIELDS,
  'trial_expires_at',
  'frozen_at',
] as const

export type OrganisationListSortKey =
  (typeof ORGANISATION_LIST_SORT_KEYS)[number]

/** How each field compares, which decides the operators it accepts. */
export type OrganisationListFieldKind = 'text' | 'enum' | 'number' | 'date'

/**
 * The `credit_plan_tier` filter value for an org with no credit plan: a test org,
 * or one awaiting its first plan (#3196). The column is null for those, so the
 * server filters and sorts on `COALESCE(credit_plan_tier, 'none')`, and every
 * operator treats "no plan" as a plan value of its own (#3211).
 */
export const NO_CREDIT_PLAN_FILTER_VALUE = 'none'

export const ORGANISATION_LIST_FIELD_KINDS: Record<
  OrganisationListFilterField,
  OrganisationListFieldKind
> = {
  name: 'text',
  credit_plan_tier: 'enum',
  lifecycle_state: 'enum',
  payment_state: 'enum',
  is_test: 'enum',
  monthly_spend_cents: 'number',
  asset_count: 'number',
  storage_bytes: 'number',
  created_at: 'date',
}

export const ORGANISATION_LIST_FILTER_OPERATORS = [
  'eq',
  'neq',
  'contains',
  'not_contains',
  'starts_with',
  'in',
  'not_in',
  'lt',
  'lte',
  'gt',
  'gte',
  'between',
] as const

export type OrganisationListFilterOperator =
  (typeof ORGANISATION_LIST_FILTER_OPERATORS)[number]

/** The operators each field kind accepts. */
export const ORGANISATION_LIST_OPERATORS_BY_KIND: Record<
  OrganisationListFieldKind,
  readonly OrganisationListFilterOperator[]
> = {
  text: ['contains', 'not_contains', 'starts_with', 'eq', 'neq'],
  enum: ['eq', 'neq', 'in', 'not_in'],
  number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between'],
  date: ['eq', 'gt', 'lt', 'between'],
}

/**
 * How many values an operator takes. `in` / `not_in` take one or more,
 * `between` exactly two, everything else exactly one.
 */
export const ORGANISATION_LIST_OPERATOR_ARITY: Record<
  OrganisationListFilterOperator,
  'one' | 'two' | 'many'
> = {
  eq: 'one',
  neq: 'one',
  contains: 'one',
  not_contains: 'one',
  starts_with: 'one',
  in: 'many',
  not_in: 'many',
  lt: 'one',
  lte: 'one',
  gt: 'one',
  gte: 'one',
  between: 'two',
}

/**
 * One filter clause. Values are always strings on the wire and are read by the
 * field's kind; {@link valuesMatchKind} is what decides whether a given value
 * is one that kind accepts. Clauses combine with AND.
 */
export type OrganisationListFilter = {
  field: OrganisationListFilterField
  operator: OrganisationListFilterOperator
  values: string[]
}

/** The page size the internal organisation list answers with by default. */
export const ORGANISATION_LIST_DEFAULT_PAGE_SIZE = 25

/** The most clauses one `filters` parameter may carry. */
export const ORGANISATION_LIST_MAX_FILTERS = 20

/** The most values one clause may list, which bounds an `in`. */
export const ORGANISATION_LIST_MAX_FILTER_VALUES = 100

export type ListOrganisationsInput = Pick<
  OffsetPaginationControls,
  'page' | 'pageSize'
> & {
  sortBy?: OrganisationListSortKey
  sortDir?: SortDirection
  filters?: OrganisationListFilter[]
}

/**
 * The orgs a user holds a membership row in — active or not unless
 * `activeOnly` narrows it, so a caller rendering "who this user belongs to"
 * doesn't name orgs they have left.
 */
export type ListOrganisationsByMemberInput = {
  userId: string
  /** Defaults to false: inactive memberships are included. */
  activeOnly?: boolean
} & Pick<OffsetPaginationControls, 'page' | 'pageSize'>

/* --------------------------- Filter values -------------------------------- */

/**
 * What a filter value may be, and how a range is read. The vocabulary above
 * says which clauses exist; this says whether a given clause carries values
 * the field will take.
 *
 * Both the service that validates a clause and the query builder that runs it
 * read a value through these, so the two cannot disagree, and a caller can
 * tell a bad clause from a good one before sending it. They are plain
 * predicates: the contract carries no validation library, and a caller may use
 * them with whichever one it has.
 */

/** The largest value a Postgres `integer` column holds. */
export const INT32_MAX = 2_147_483_647

/** A whole number in plain decimal digits: no exponent, radix, space or sign but `-`. */
export const INTEGER = /^-?\d+$/

/** A value with no time of day names a whole calendar day (UTC). */
export const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

/** The years a Postgres `timestamp` takes in the four-digit form the builder binds. */
const MIN_YEAR = 1
const MAX_YEAR = 9999

/** Midnight UTC on a `YYYY-MM-DD`, the instant every date-only value starts at. */
const utcMidnight = (day: string): Date => new Date(`${day}T00:00:00.000Z`)

/** The same clock time one day later, which is where a date-only day ends. */
const nextDay = (instant: Date): Date => {
  const next = new Date(instant)
  next.setUTCDate(next.getUTCDate() + 1)
  return next
}

const yearInRange = (instant: Date): boolean => {
  const year = instant.getUTCFullYear()
  return year >= MIN_YEAR && year <= MAX_YEAR
}

export const startOfDay = (value: string): string =>
  utcMidnight(value).toISOString()

export const startOfNextDay = (value: string): string =>
  nextDay(utcMidnight(value)).toISOString()

/** Whether `YYYY-MM-DD` names a day the calendar has, which `2026-02-30` does not. */
export const isCalendarDay = (day: string): boolean => {
  const instant = utcMidnight(day)
  return (
    !Number.isNaN(instant.getTime()) && instant.toISOString().startsWith(day)
  )
}

/**
 * Whether a value is a date this list will filter on.
 *
 * A whole calendar day, `YYYY-MM-DD`, and nothing finer. The console's filter
 * is a date picker and can send nothing else, and a day is the unit support
 * actually asks in. Accepting an instant as well meant carrying a second
 * format, a precision rule and an offset rule for input no screen produces,
 * and a range whose upper bound was inclusive or exclusive depending on which
 * form it arrived in.
 *
 * Both ends are checked: an upper bound runs to the following midnight, so the
 * day after must also be a year Postgres takes.
 */
export const isFilterDate = (value: string): boolean => {
  if (!DATE_ONLY.test(value) || !isCalendarDay(value)) return false
  const midnight = utcMidnight(value)
  return yearInRange(midnight) && yearInRange(nextDay(midnight))
}

/**
 * The instants a date `between` selects, as UTC ISO strings. Both bounds name
 * whole days, so the range starts when the lower day starts and ends, always
 * exclusively, where the day after the upper one starts.
 */
export const dateBetweenBounds = (
  first: string,
  second: string,
): { lower: string; upper: string } => ({
  lower: startOfDay(first),
  upper: startOfNextDay(second),
})

/**
 * Whether a `between` clause selects anything, by the field's kind. A date
 * range is measured as the query builder reads it, where the upper day runs
 * to its end: naming the same day twice is a range of one day, not an empty
 * one.
 */
export const boundsAscend = (
  field: OrganisationListFilterField,
  [low, high]: string[],
): boolean => {
  if (low === undefined || high === undefined) return true
  if (ORGANISATION_LIST_FIELD_KINDS[field] !== 'date') {
    return Number(low) <= Number(high)
  }
  const { lower, upper } = dateBetweenBounds(low, high)
  return Date.parse(lower) < Date.parse(upper)
}

/**
 * Whether every value in a clause parses as its field's kind. A well-formed
 * but out-of-range number is a bad request rather than a database error.
 */
export const valuesMatchKind = (
  field: OrganisationListFilterField,
  values: string[],
): boolean => {
  switch (ORGANISATION_LIST_FIELD_KINDS[field]) {
    case 'number':
      // `Number` alone reads a blank as 0 and `0x10` as 16, so the digits are
      // checked first.
      return values.every((value) => {
        if (!INTEGER.test(value)) return false
        const parsed = Number(value)
        return (
          Number.isSafeInteger(parsed) &&
          (field === 'storage_bytes' || Math.abs(parsed) <= INT32_MAX)
        )
      })
    case 'date':
      return values.every(isFilterDate)
    case 'enum':
      return (
        field !== 'is_test' ||
        values.every((value) => value === 'true' || value === 'false')
      )
    case 'text':
      return true
  }
}
